// Performance.ts
import { ProductType } from '../../Enums/ChoiceEnums';
import { ChoiceApi } from '../../LibMain';
import { getObject, ErrorCallbackFunction, postObject, ApiResponseSimple } from '../ChoiceApi';

let enableDegradedAPIPerformanceAudit: boolean = false;
const logEntryThresholdInMilliSeconds = 2000;
const degradedCallPrefix = "OMDRequest:";
const maxBodyLength = 10*1024;
const degradedApiLoggingEndpoint = "api/Performance/Degraded";

export async function initializeDegradedAPILogging(intervalTimeInMilliSec?: number) {
    if (!await ChoiceApi.companySettings.isProductActive(ProductType.PerformanceAudit))
        return;

    // Indicate to the logging function that we're ready to accept audit data
    enableDegradedAPIPerformanceAudit = true;

    // Start processing the queue
    window.setInterval(processDegradedAPIPerformanceDataQueue, intervalTimeInMilliSec ?? 60000);                
}

// Send the data we've collected to the server
async function processDegradedAPIPerformanceDataQueue()
{
    for (let key in sessionStorage) {
        if (key.includes(degradedCallPrefix)) {
            let telemetryData = JSON.parse(sessionStorage.getItem(key)!);

            // Should we log the entry?
            if (logThisEntry(telemetryData))
            {
                // Make sure our call worked
                if (!await saveDegradedRequestData(telemetryData, err => { /* Swallow errors */}))
                    continue;
            }

            // Clear the entry from the session storage object
            sessionStorage.removeItem(key);
        }
    }
}

function logThisEntry(degradedRequestModel: DegradedRequestModel): boolean
{
    // Only log degraded API request data if logging is enabled
    if (!enableDegradedAPIPerformanceAudit)
        return false;

    // Don't log calls that are intended to track degraded API calls
    if (degradedRequestModel.endpointPath.endsWith(degradedApiLoggingEndpoint))
        return false;

    // Don't log 502 (Bas Gateway) error -- means the service isn't running (not slow)
    if (502 === degradedRequestModel.httpStatus)
        return false;    

    // Never completed?
    if (undefined === degradedRequestModel.completedTimeEpoch)
        return true;

    // Took too long
    const elapsedTimeInMilliSeconds = degradedRequestModel.completedTimeEpoch - degradedRequestModel.openTimeEpoch;
    if (elapsedTimeInMilliSeconds > logEntryThresholdInMilliSeconds)
        return true
    return false;
}

// Add to the queue of degraded API data. We'll upload periodically
export function addDegradedRequestDataItem(degradedRequestModel: DegradedRequestModel)
{
	// Should we log this? Currently the threshold is hard coded for 2 seconds.  We should probably make this configurable.
	if (!logThisEntry(degradedRequestModel))
    {
		return;
    }

    // Limit the size of the API body to 10k.  
    //  We don't want to track the data as much as the intent. In most cases we only need a reasonable amount of the data.
    if ((degradedRequestModel.body?.length ?? 0) > maxBodyLength)
    {
        degradedRequestModel.body = degradedRequestModel.body?.substring(0, maxBodyLength)
    }

    const key = degradedCallPrefix + crypto.randomUUID();
    sessionStorage.setItem(key, JSON.stringify(degradedRequestModel));
}

// Return an error if exists, undefined otherwise
export async function saveDegradedRequestData(degradedRequestModel: DegradedRequestModel, errorCallback?: ErrorCallbackFunction): Promise<boolean | undefined> {
    // Limit the body data we log to ~10k
    if ((degradedRequestModel.body?.length ?? 0) > maxBodyLength) {
        degradedRequestModel.body = degradedRequestModel.body?.substring(0, maxBodyLength);
    }

    const result = await postObject<ApiResponseSimple<boolean>>(degradedApiLoggingEndpoint, degradedRequestModel, { errorCallback: errorCallback });
    return result?.success;
}

export async function getApiPerformanceResults(startDateTime: Date, minutesOfResults: number, userId?: string): Promise<DegradedRequestProcessedModel[] | undefined>
{
    const startTimeInUnixMS = startDateTime.getTime();
    const endTimeInUnixMS = startTimeInUnixMS + minutesOfResults * 60 * 1000;

    return await getObject(`api/Performance/Results/${startTimeInUnixMS}/${endTimeInUnixMS}/${userId ?? ''}`);
}

export interface DegradedRequestModel
{
    method: string,
    endpointPath: string,
    body?: string,
    handler?: string,
    openTimeEpoch: number,
    sendTimeEpoch?: number,     // Not availble for fetch()
    completedTimeEpoch?: number,
    httpStatus?: number
};

export interface DegradedRequestProcessedModel
{
    method: string,
    endpointPath: string,
    body?: string,
    databaseServer?: string,
    companyName?: string,
    userId?: string,
    startTime?: Date,
    openToSendTimeMS?: number,
    sendToCompletedMS?: number,
    openToCompletedMS?: number
};